/**
 * Farbprofil des Mandanten.
 *
 * Die Farbwerte stehen in `styles/theme.css` unter `:root[data-color='…']`;
 * hier steht nur, welche Namen es gibt und wie sie ans Wurzelelement kommen.
 *
 * Anders als das Hell/Dunkel-Thema ist das Farbprofil **keine Einstellung des
 * Benutzers**: Es gehört dem Mandanten und kommt mit `/api/auth/me`. Deshalb
 * liegt es auch nicht im Browserspeicher – wer den Mandanten wechselt, soll
 * die Farbe des neuen Hauses sehen, nicht die zuletzt gemerkte.
 */

export const COLOR_PROFILES = [
  { key: 'blau', label: 'Blau' },
  { key: 'rot', label: 'Rot' },
  { key: 'orange', label: 'Orange' },
  { key: 'gelb', label: 'Gelb' },
  { key: 'gruen', label: 'Grün' },
  { key: 'violett', label: 'Violett' },
  { key: 'pink', label: 'Pink' },
] as const;

export type ColorProfile = (typeof COLOR_PROFILES)[number]['key'];

export const DEFAULT_COLOR_PROFILE: ColorProfile = 'blau';

/** Einstellungsschlüssel – muss zu `backend/src/lib/colorProfile.ts` passen. */
export const COLOR_PROFILE_KEY = 'ui.colorProfile';

export function isColorProfile(value: unknown): value is ColorProfile {
  return typeof value === 'string' && COLOR_PROFILES.some((profil) => profil.key === value);
}

export function labelFor(value: unknown): string {
  return COLOR_PROFILES.find((profil) => profil.key === value)?.label ?? 'Blau';
}

/**
 * Setzt das Profil am Wurzelelement.
 *
 * Blau steht in theme.css als Grundsatz und hat keinen eigenen
 * `[data-color]`-Block; dafür wird das Attribut entfernt statt gesetzt.
 */
export function applyColorProfile(value: unknown): void {
  const profil = isColorProfile(value) ? value : DEFAULT_COLOR_PROFILE;
  if (profil === DEFAULT_COLOR_PROFILE) delete document.documentElement.dataset.color;
  else document.documentElement.dataset.color = profil;
}

/**
 * Zuletzt gesehenes Profil – **nur für die Anmeldeseite**.
 *
 * Das Profil gehört weiterhin dem Mandanten und wird nach dem Anmelden immer
 * frisch aus `/api/auth/me` gesetzt; wer den Mandanten wechselt, sieht sofort
 * die neue Hausfarbe. Gemerkt wird es allein für den Zustand *ohne* Konto:
 * Nach dem Abmelden sprang die Oberfläche bisher auf Blau zurück, was wie ein
 * Fehler aussah. Dieselbe Lösung führt die mobile Oberfläche bereits, dort für
 * den Start ohne Netzverbindung (`frontend-mobile/src/lib/colorProfile.ts`).
 */
const SPEICHER_SCHLUESSEL = 'ff.colorProfile';

/** Vom Server übernehmen und für die nächste Anmeldeseite merken. */
export function rememberColorProfile(value: unknown): void {
  const profil = isColorProfile(value) ? value : DEFAULT_COLOR_PROFILE;
  try {
    localStorage.setItem(SPEICHER_SCHLUESSEL, profil);
  } catch {
    /* Privater Modus oder gesperrter Speicher – die Farbe ist es nicht wert. */
  }
  applyColorProfile(profil);
}

/** Ohne Konto: das zuletzt bekannte Profil setzen statt auf Blau zu fallen. */
export function applyCachedColorProfile(): void {
  let gemerkt: string | null = null;
  try {
    gemerkt = localStorage.getItem(SPEICHER_SCHLUESSEL);
  } catch {
    /* siehe oben */
  }
  applyColorProfile(gemerkt);
}
